import * as React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { getValuesForBezierCurve, normalize, range } from '@/utils';
import { DEMO_COLORS_DARK } from '@/constants/Wham.colors';

import Space from '@/components/Space';
import ToggleButtonGroup from '@/components/SleekToggleButtonGroup';

import KeyframeGraph from './KeyframeGraph';

interface Props {}

type Mode = 'twinkle' | 'fade-in' | 'twinkle-fade-in';

// So, this graph shows how a `fadeFromTransparent` curve can affect a back-and-forth `twinkle` curve. Originally, the Bezier curve covered the entire thing, but this obscured the fact that the `fadeFromTransparent` happens at the beginning and then things continue as normal. So, we can control what percentage of the graph is eased with this variable:
const CURVE_COVERAGE = 0.75;
const CURVE_WIDTH = 500 * CURVE_COVERAGE;

const CONTROL_POINTS = {
  startPoint: [0, 0],
  controlPoint1: [CURVE_WIDTH * 0.35, 0],
  controlPoint2: [CURVE_WIDTH * 0.65, 150],
  endPoint: [CURVE_WIDTH, 150],
};

function ToDynamicGraph({}: Props) {
  const [mode, setMode] = React.useState<Mode>('twinkle');

  const path = calculatePath(mode);
  const spring = useSpring({
    d: path,
  });

  return (
    <Wrapper>
      <ToggleButtonGroup
        title="Animation:"
        options={[
          {
            label: 'twinkle',
            value: 'twinkle',
          },
          {
            label: 'fadeFromTransparent',
            value: 'fade-in',
          },
          {
            label: 'twinkle + fadeFromTransparent',
            value: 'twinkle-fade-in',
          },
        ]}
        value={mode}
        handleSelect={(value) => {
          // TypeScript thinks `value` is a string. If I try to change it to Mode, the handleSelect function complains. So, whatever.
          // @ts-expect-error
          setMode(value);
        }}
      />
      <StyledKeyframeGraph>
        <Svg viewBox="0 0 500 150" preserveAspectRatio="none">
          <Line d={spring.d} fill="none" />
        </Svg>
      </StyledKeyframeGraph>
    </Wrapper>
  );
}

const NUM_OF_PEAKS = 15;

function calculatePath(mode: Mode) {
  if (mode === 'twinkle') {
    return range(NUM_OF_PEAKS + 1).reduce(
      (acc: string, i: number) => {
        const isUpper = i % 2 !== 0;
        const x = 500 * (i / NUM_OF_PEAKS);
        const y = 150 * (isUpper ? 0.75 : 0.25);
        const command = i === 0 ? 'M' : 'L';
        return `${acc} ${command} ${x} ${y}`;
      },
      ''
    );
  } else if (mode === 'fade-in') {
    return range(NUM_OF_PEAKS + 1).reduce(
      (acc: string, i: number) => {
        const isUpper = i % 2 !== 0;
        const x = 500 * (i / NUM_OF_PEAKS);
        let y = 150 * (isUpper ? 0.75 : 0.25);

        const maxWidthUnderCurve = 500 * CURVE_COVERAGE;

        if (x < maxWidthUnderCurve) {
          const result = getValuesForBezierCurve(
            // @ts-expect-error
            CONTROL_POINTS,
            normalize(x, 0, maxWidthUnderCurve, 0, 1)
          );
          y = result[1];
        } else {
          y = 150;
        }
        const command = i === 0 ? 'M' : 'L';
        return `${acc} ${command} ${x} ${y}`;
      },
      ''
    );
  } else {
    return range(NUM_OF_PEAKS + 1).reduce(
      (acc: string, i: number) => {
        const isUpper = i % 2 !== 0;
        const x = 500 * (i / NUM_OF_PEAKS);
        let y = 150 * (isUpper ? 0.75 : 0.25);

        const maxWidthUnderCurve = 500 * CURVE_COVERAGE;

        if (x < maxWidthUnderCurve) {
          const result = getValuesForBezierCurve(
            // @ts-expect-error
            CONTROL_POINTS,
            normalize(x, 0, maxWidthUnderCurve, 0, 1)
          );
          y = y * normalize(result[1], 0, 150, 0, 1);
        }
        const command = i === 0 ? 'M' : 'L';
        return `${acc} ${command} ${x} ${y}`;
      },
      ''
    );
  }
}

const Wrapper = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 1rem;
  margin-bottom: 4rem;
  padding: 1rem;
  padding-bottom: 4rem;
  border-radius: 16px;
`;

const StyledKeyframeGraph = styled(KeyframeGraph)`
  margin-top: 1rem;
`;

const Svg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: scaleY(-1);
  overflow: visible;
`;

const Line = styled(animated.path)`
  stroke: hsl(50deg 100% 50%);
  stroke: ${DEMO_COLORS_DARK.primary500};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export default ToDynamicGraph;
